/**
 * useConfirmedAuth — the ONE authoritative "is a real user signed in" signal.
 *
 * 2026-08-15 owner incident (second occurrence): portal pages rendered for a
 * signed-out visitor because route guards accepted the localStorage-cached
 * AuthContext identity — a cache backed by a client-minted random token that
 * nothing could ever expire — OR'd with a one-shot Firebase probe that never
 * re-evaluated after mount. This hook is the shared replacement:
 *
 *   • Firebase's onAuthStateChanged is the ONLY authority — no localStorage.
 *   • The subscription stays LIVE, so a mid-session sign-out or account
 *     switch re-evaluates every consumer (pattern proven in
 *     JusticeAccessGate; extracted here for reuse).
 *   • Anonymous users never count as signed in.
 *   • Firebase init/observer failure fails CLOSED (signed out).
 *   • `checked` is the hydration gate — render a neutral loading beat until
 *     it flips true, never a guess.
 */
import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';

export interface ConfirmedAuthState {
  /** First Firebase resolution has landed — until then show a loading beat. */
  checked: boolean;
  /** A real (non-anonymous) Firebase user is signed in RIGHT NOW. */
  signedIn: boolean;
  /** The confirmed user's uid, else null. */
  uid: string | null;
  /** The confirmed user's email, else null. */
  email: string | null;
}

const SIGNED_OUT: Omit<ConfirmedAuthState, 'checked'> = { signedIn: false, uid: null, email: null };

export function useConfirmedAuth(): ConfirmedAuthState {
  const [state, setState] = useState<ConfirmedAuthState>({ checked: false, ...SIGNED_OUT });

  useEffect(() => {
    try {
      const auth = getAuth();
      const unsub = onAuthStateChanged(
        auth,
        (u: FirebaseUser | null) => {
          const real = !!u && !u.isAnonymous;
          setState({
            checked: true,
            signedIn: real,
            uid: real && u ? u.uid : null,
            email: real && u ? (u.email || null) : null,
          });
        },
        () => setState({ checked: true, ...SIGNED_OUT }),
      );
      return () => { try { unsub(); } catch { /* ignore */ } };
    } catch {
      setState({ checked: true, ...SIGNED_OUT });
      return undefined;
    }
  }, []);

  return state;
}

export default useConfirmedAuth;
